// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — one round of a live race, decoupled from Trystero, the DOM and the clock
// (driven by guess()/tick(), which is what makes the host-transfer contract testable
// without a network — tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer holds its own copy of the identical pair
// sequence (a pure function of the frozen seed) and answers independently. NO game
// state crosses the wire — only a tiny status gossip (streak, done, and on finishing
// the fatal pair + pick, so the results can show what each player missed). Each peer
// reaches its OWN game-over (a wrong pick / a pair timing out) with no host help, so
// HOST TRANSFER IS A PURE DISPLAY CONCERN. onHostChange is wired regardless.

import { createGame, fatalAnswer, guess, tickTimer, type GameState, type Side } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  streak: number;
  over: boolean;
  /** Sequence index of the pair that ended the run (for the reveal), or -1. */
  missedIdx: number;
  /** Side picked on the fatal pair: 0 left, 1 right, -1 timeout / not yet finished. */
  missedPick: number;
  left: boolean;
}

/** Wire status. Tiny; the fatal-pair detail rides only on the final message. */
export interface RaceMsg {
  k: number; // streak
  d: boolean; // done
  mp?: number; // fatal pair index
  pk?: number; // picked side on the fatal pair (0/1/-1)
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

const sideToPick = (s: Side | null): number => (s === 'left' ? 0 : s === 'right' ? 1 : -1);

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed, true); // versus is timed per pair
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, {
        seat,
        name,
        streak: 0,
        over: false,
        missedIdx: -1,
        missedPick: -1,
        left: false,
      });
    });
    if (o.selfSeat !== null) this.syncSelf();
  }

  get isHost(): boolean {
    return this.host;
  }
  get msLeft(): number {
    return Math.max(0, this.state.pairMsLeft);
  }

  private self(): PeerResult | undefined {
    return this.selfSeat === null ? undefined : this.results.get(this.selfSeat);
  }

  private syncSelf(): void {
    const r = this.self();
    if (!r) return;
    r.streak = this.state.streak;
    r.over = this.state.over;
    if (this.state.over) {
      const fa = fatalAnswer(this.state);
      r.missedIdx = fa ? fa.idx : -1;
      r.missedPick = fa ? sideToPick(fa.picked) : -1;
    }
  }

  /** Play a local pick; returns whether it changed anything. */
  guess(side: Side): boolean {
    if (this.selfSeat === null || this.state.over) return false;
    const next = guess(this.state, side);
    if (next === this.state) return false;
    this.state = next;
    this.syncSelf();
    if (this.state.over) return this.finishSelf();
    this.broadcastProgress();
    this.deps.onChange();
    return true;
  }

  private broadcastProgress(): void {
    const r = this.self();
    if (!r) return;
    this.deps.send({ k: r.streak, d: false });
  }

  private finishSelf(): boolean {
    if (this.ended) return true;
    this.ended = true;
    this.syncSelf();
    const r = this.self();
    if (r) this.deps.send({ k: r.streak, d: true, mp: r.missedIdx, pk: r.missedPick });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
    return true;
  }

  /** A status update from another peer. */
  receive(msg: RaceMsg, fromSeat: number): void {
    const r = this.results.get(fromSeat);
    if (!r || r.seat === this.selfSeat) return;
    r.streak = Math.max(r.streak, msg.k);
    if (msg.d) {
      r.over = true;
      if (typeof msg.mp === 'number') r.missedIdx = msg.mp;
      if (typeof msg.pk === 'number') r.missedPick = msg.pk;
    }
    this.deps.onChange();
  }

  /** Interval-driven clock (never rAF alone). Every seated peer runs its own. */
  tick(dtMs: number): void {
    if (this.selfSeat === null || this.state.over) return;
    const next = tickTimer(this.state, dtMs);
    if (next === this.state) return;
    this.state = next;
    this.syncSelf();
    if (this.state.over) this.finishSelf();
    else this.deps.onChange();
  }

  /** Promotion carries no authoritative duty here — recorded for display only. */
  setHost(isHost: boolean): void {
    this.host = isHost;
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (!r || seat === this.selfSeat) return;
    r.left = true;
    r.over = true; // a departed peer will never finish; don't hold the room for it
    this.deps.onChange();
  }
  peerReturned(seat: number): void {
    const r = this.results.get(seat);
    if (r) r.left = false;
  }

  everyoneDone(): boolean {
    return [...this.results.values()].every((r) => r.over);
  }

  /** Results best-first: highest streak wins, departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      if (a.left !== b.left) return a.left ? 1 : -1;
      return b.streak - a.streak;
    });
  }
}

/** Seat index from the frozen roster, or null for a spectator / unknown peer. */
export function seatOf(roster: Array<{ id: string }>, peerId: string): number | null {
  const i = roster.findIndex((p) => p.id === peerId);
  return i < 0 ? null : i;
}

/** A one-shot timer safe to poke repeatedly — re-arming on every poll starves it. */
export function createOneShot(delayMs: number, run: () => void) {
  let id: ReturnType<typeof setTimeout> | null = null;
  return {
    poke(): void {
      if (id !== null) return;
      id = setTimeout(() => {
        id = null;
        run();
      }, delayMs);
    },
    cancel(): void {
      if (id !== null) clearTimeout(id);
      id = null;
    },
  };
}
